import React, { useCallback, useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { useDispatch, useSelector } from 'react-redux'

import {
  selectScopedChatParticipatingAgents,
  selectByMatrixId,
} from 'ducks/chat/selectors/presence'
import { selectCurrentUserIsAdmin } from 'ducks/currentUser'
import { doUpdatePresenceForMatrixUsers } from 'ducks/chat/actions/presence'
import { selectIsMatrixClientReady } from 'ducks/chat/selectors/client'
import { doHideChatPresenceOverviewModal } from 'actions/modals'
import { selectAgentCount } from 'selectors/agents/base'

import {
  Form,
  Dropdown,
  Segment,
  Label,
  Button,
  Checkbox,
  Loader,
} from 'util/ui'
import Sidebar from 'shared/components/ui/Sidebar'
import SpacedItems from 'shared/components/ui/SpacedItems'
import { SortableList, Avatar } from 'shared/ui'

import { SortableListItem } from 'shared/ui/SortableList'
import MessageBox from 'subapps/settings/components/Widgets/Settings/MessageBox'

const agentNameStyle = () => theme => css`
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.color.monochrome.dark} !important;
`

const paragraphStyle = () => theme => css`
  &&& {
    font-size: ${theme.fontSize.x2large};
    color: ${theme.color.monochrome.dark};
    margin-bottom: ${theme.spacing['12']};
  }
`

const optionLineStyle = () => theme => css`
  display: flex;
  align-items: center;
  & > .ui.label {
    margin-right: ${theme.spacing['5']} !important;
  }
`

const indicatorStyle = primaryColor => theme => css`
  &&& {
    background-color: ${theme.color.primary[primaryColor]};
    border-color: ${theme.color.primary[primaryColor]};
  }
`

const downgradeButtonStyle = () => theme => css`
  cursor: pointer;
  color: ${theme.color.primary.negative};
`

const downgradeButtonHeaderStyle = () => theme => css`
  letter-spacing: normal;
  font-size: ${theme.fontSize.base};
  font-weight: ${theme.fontWeight.normal};
  text-transform: none;
  text-align: right;
  position: absolute; /* Keeps this from changing the header height */
  top: -10px;
  right: 0;
  white-space: nowrap;
`

const checkboxStyle = css`
  &&& {
    display: block;
  }
`

const headerCheckboxStyle = css`
  .sui &.ui.checkbox input:not([type='radio']):indeterminate ~ .box:after,
  .sui &.ui.checkbox input:not([type='radio']):indeterminate ~ label:after {
    left: 1px;
    width: 16px;
    height: 16px;
  }
`

const containerStyle = () => theme => css`
  padding: ${[
    theme.spacing['18'],
    theme.spacing['12'],
    theme.spacing['12'],
    theme.spacing['12'],
  ].join(' ')};
  height: 100%;
`

const statuses = {
  autoPresence: (
    <span css={optionLineStyle}>
      <Label circular empty css={indicatorStyle('warning')} /> Auto-away
    </span>
  ),
  online: (
    <span css={optionLineStyle}>
      <Label circular empty css={indicatorStyle('positive')} /> Online
    </span>
  ),

  unavailable: (
    <span css={optionLineStyle}>
      <Label circular empty css={indicatorStyle('warning')} /> Away
    </span>
  ),

  offline: (
    <span css={optionLineStyle}>
      <Label circular empty css={indicatorStyle('negative')} /> Offline
    </span>
  ),
}

const sortHeaders = [
  {
    title: 'Agents',
    key: 'agents',
    sortKey: 'name',
    sortable: true,
  },
]

const PresenceOverviewModalContent = () => {
  const chatAgents = useSelector(selectScopedChatParticipatingAgents)
  const fullAgentCount = useSelector(selectAgentCount)
  const chatAgentCount = chatAgents.length
  const showAgentNotice = chatAgents.length !== fullAgentCount
  const presenceByMatrixId = useSelector(selectByMatrixId)
  const isAdmin = useSelector(selectCurrentUserIsAdmin)
  const dispatch = useDispatch()

  const [selected, setSelected] = useState({})
  const numSelected = useMemo(
    () => {
      return Object.values(selected).filter(Boolean).length
    },
    [selected]
  )

  const handleCheckboxChange = useCallback(
    (_, { name: chatId, checked }) => {
      setSelected(currentSelected => {
        return {
          ...currentSelected,
          [chatId]: checked,
        }
      })
    },
    [setSelected]
  )

  const handleBulkCheckboxChange = useCallback(
    (_, { checked }) => {
      if (checked) {
        setSelected(
          chatAgents.reduce((memo, agent) => {
            // eslint-disable-next-line no-param-reassign
            memo[agent.chatId] = true
            return memo
          }, {})
        )
      } else {
        setSelected({})
      }
    },
    [chatAgents]
  )

  const handlePresenceDowngrade = useCallback(
    agentChatId => {
      dispatch(doUpdatePresenceForMatrixUsers([agentChatId], 'offline'))
    },
    [dispatch]
  )

  const handleBulkPresenceDowngrade = useCallback(
    () => {
      dispatch(
        doUpdatePresenceForMatrixUsers([Object.keys(selected)], 'offline')
      )
      setSelected({})
    },
    [dispatch, setSelected, selected]
  )

  const headerPrefix = useMemo(
    () => {
      if (!isAdmin) return null
      return (
        <Checkbox
          indeterminate={numSelected > 0 && numSelected < chatAgentCount}
          checked={numSelected === chatAgentCount}
          onChange={handleBulkCheckboxChange}
          css={[checkboxStyle, headerCheckboxStyle]}
        />
      )
    },
    [isAdmin, numSelected, chatAgentCount, handleBulkCheckboxChange]
  )

  const headerSuffix = useMemo(
    () => {
      if (!isAdmin || !numSelected) return null
      return (
        <Form.Field
          css={css`
            position: relative;
          `}
        >
          <span
            role="button"
            tabIndex="0"
            css={[downgradeButtonStyle, downgradeButtonHeaderStyle]}
            onClick={handleBulkPresenceDowngrade}
          >
            Set {numSelected} selected to offline
          </span>
        </Form.Field>
      )
    },
    [isAdmin, numSelected, handleBulkPresenceDowngrade]
  )

  return (
    <React.Fragment>
      <p css={paragraphStyle}>
        Conveniently see {isAdmin && 'and manage '}the chat presence of your
        team.
      </p>
      {showAgentNotice && (
        <MessageBox
          dismissFlag="chatPresenceOverviewAgentCount"
          icon="warning"
          info
        >
          Agents that do not have access to any chat widgets are not listed
          here.
        </MessageBox>
      )}
      <SortableList
        headers={sortHeaders}
        headerPrefix={headerPrefix}
        headerSuffix={headerSuffix}
        initialSortKey="name"
      >
        {chatAgents.map(agent => (
          <SortableListItem key={agent.chatId} name={agent.name}>
            <Segment raised>
              <SpacedItems.Container verticalAlign="center">
                {isAdmin && (
                  <SpacedItems.Item>
                    <Checkbox
                      name={agent.chatId}
                      checked={!!selected[agent.chatId]}
                      onChange={handleCheckboxChange}
                      css={checkboxStyle}
                    />
                  </SpacedItems.Item>
                )}
                <SpacedItems.Item>
                  <Avatar actor={agent} size={24} initialsFontSize={13} />
                </SpacedItems.Item>
                <SpacedItems.Item css={agentNameStyle}>
                  {agent.name}
                </SpacedItems.Item>
                <SpacedItems.Item basis="96px" gap="auto">
                  {statuses[presenceByMatrixId[agent.chatId] || 'unavailable']}
                </SpacedItems.Item>
                {isAdmin && (
                  <SpacedItems.Item>
                    <Dropdown icon="ellipsis vertical" direction="left">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          // eslint-disable-next-line react/jsx-no-bind
                          onClick={() => handlePresenceDowngrade(agent.chatId)}
                        >
                          <span css={downgradeButtonStyle}>
                            Change to offline
                          </span>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </SpacedItems.Item>
                )}
              </SpacedItems.Container>
            </Segment>
          </SortableListItem>
        ))}
      </SortableList>
    </React.Fragment>
  )
}

const PresenceOverviewModal = () => {
  const isMatrixClientReady = useSelector(selectIsMatrixClientReady)
  const dispatch = useDispatch()
  const handleClose = useCallback(
    () => {
      dispatch(doHideChatPresenceOverviewModal())
    },
    [dispatch]
  )

  return (
    <Sidebar
      onClose={handleClose}
      open
      headerChildren="Team presence"
      footerChildren={
        <Button fluid primary basic onClick={handleClose}>
          Close
        </Button>
      }
    >
      <div css={containerStyle}>
        <SpacedItems.Container
          direction="vertical"
          css={css`
            height: 100%;
          `}
        >
          <SpacedItems.Item
            grow={1}
            css={css`
              padding-bottom: 200px;
            `}
          >
            {isMatrixClientReady ? (
              <PresenceOverviewModalContent />
            ) : (
              <Loader active inline="centered" />
            )}
          </SpacedItems.Item>
        </SpacedItems.Container>
      </div>
    </Sidebar>
  )
}

export default PresenceOverviewModal
